import jsPDF from 'jspdf';
import { Gasto, totalDoGasto } from './gastosData';
import { formatCurrency } from '../formularioParts/utils';

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 14;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 18;
const ROW_H = 7;
const CATEGORIA_H = 9;
const SUBTOTAL_H = 8;

/** Larguras das colunas em mm — soma deve caber em PAGE_W - MARGIN_X * 2 (182mm). */
const COLS = {
  data: { x: MARGIN_X, w: 22, label: 'Data', align: 'left' as const },
  descricao: { x: MARGIN_X + 22, w: 68, label: 'Descrição', align: 'left' as const },
  quantidade: { x: MARGIN_X + 90, w: 18, label: 'Qtd', align: 'right' as const },
  valorUnitario: { x: MARGIN_X + 108, w: 34, label: 'Valor unit.', align: 'right' as const },
  total: { x: MARGIN_X + 142, w: 40, label: 'Total', align: 'right' as const }
};

const formatarDataCurta = (valor: string) => {
  if (!valor) return '-';
  const [ano, mes, dia] = valor.split('-');
  return dia && mes ? `${dia}/${mes}/${ano}` : valor;
};

const escreverCelula = (
  doc: jsPDF,
  texto: string,
  coluna: { x: number; w: number; align: 'left' | 'right' },
  y: number
) => {
  const x = coluna.align === 'right' ? coluna.x + coluna.w : coluna.x;
  doc.text(texto, x, y, { align: coluna.align, maxWidth: coluna.w });
};

const desenharCabecalhoPagina = (doc: jsPDF, ano: string, pagina: number, totalPaginas: number) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text('Relatório de Produtos e Gastos', MARGIN_X, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(`Colônia de Férias - Período ${ano}`, MARGIN_X, 22);

  doc.setFontSize(9);
  doc.text(`Página ${pagina} de ${totalPaginas}`, PAGE_W - MARGIN_X, 16, { align: 'right' });
};

const desenharCabecalhoTabela = (doc: jsPDF, y: number) => {
  doc.setFillColor(232, 243, 251);
  doc.rect(MARGIN_X, y - 5, PAGE_W - MARGIN_X * 2, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(31, 111, 159);
  Object.values(COLS).forEach((coluna) => escreverCelula(doc, coluna.label, coluna, y));
};

const desenharResumo = (
  doc: jsPDF,
  y: number,
  receita: number,
  totalGastos: number,
  saldo: number
) => {
  const largura = (PAGE_W - MARGIN_X * 2 - 12) / 3;
  const cartoes: { label: string; valor: string; cor: [number, number, number] }[] = [
    { label: 'RECEITA', valor: formatCurrency(receita), cor: [39, 174, 96] },
    { label: 'GASTOS', valor: formatCurrency(totalGastos), cor: [239, 68, 68] },
    { label: 'SALDO', valor: formatCurrency(saldo), cor: saldo >= 0 ? [39, 174, 96] : [239, 68, 68] }
  ];

  cartoes.forEach((cartao, index) => {
    const x = MARGIN_X + index * (largura + 6);
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, largura, 20, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(cartao.label, x + largura / 2, y + 7, { align: 'center' });

    doc.setFontSize(13);
    const [r, g, b] = cartao.cor;
    doc.setTextColor(r, g, b);
    doc.text(cartao.valor, x + largura / 2, y + 15, { align: 'center' });
  });

  return y + 20 + 10;
};

type Grupo = { categoria: string; itens: Gasto[]; subtotal: number };

export const gerarPdfGastos = (
  gastos: Gasto[],
  ano: string,
  receita: number
): jsPDF => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const totalGastos = gastos.reduce((sum, item) => sum + totalDoGasto(item), 0);
  const saldo = receita - totalGastos;

  const grupos: Grupo[] = Object.values(
    gastos.reduce<Record<string, Grupo>>((acc, gasto) => {
      const chave = gasto.categoria || 'Outros';
      if (!acc[chave]) acc[chave] = { categoria: chave, itens: [], subtotal: 0 };
      acc[chave].itens.push(gasto);
      acc[chave].subtotal += totalDoGasto(gasto);
      return acc;
    }, {})
  ).sort((a, b) => b.subtotal - a.subtotal);

  let paginaAtual = 1;
  const totalPaginasEstimado = 1;
  desenharCabecalhoPagina(doc, ano, paginaAtual, totalPaginasEstimado);
  let y = desenharResumo(doc, MARGIN_TOP + 6, receita, totalGastos, saldo);
  desenharCabecalhoTabela(doc, y);
  y += ROW_H;

  const quebrarPaginaSeNecessario = (proximaAlturaLinha: number) => {
    if (y + proximaAlturaLinha <= PAGE_H - MARGIN_BOTTOM) return;
    doc.addPage();
    paginaAtual += 1;
    desenharCabecalhoPagina(doc, ano, paginaAtual, totalPaginasEstimado);
    y = MARGIN_TOP + 6;
    desenharCabecalhoTabela(doc, y);
    y += ROW_H;
  };

  if (grupos.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text('Nenhum gasto lançado neste período.', MARGIN_X, y + 6);
  }

  grupos.forEach((grupo) => {
    quebrarPaginaSeNecessario(CATEGORIA_H);
    doc.setFillColor(248, 250, 252);
    doc.rect(MARGIN_X, y - 5, PAGE_W - MARGIN_X * 2, CATEGORIA_H - 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(grupo.categoria, MARGIN_X + 2, y);
    doc.setTextColor(100, 116, 139);
    doc.text(`${grupo.itens.length} ${grupo.itens.length === 1 ? 'item' : 'itens'}`, PAGE_W - MARGIN_X - 2, y, {
      align: 'right'
    });
    y += CATEGORIA_H;

    grupo.itens.forEach((gasto, index) => {
      quebrarPaginaSeNecessario(ROW_H);
      if (index % 2 === 0) {
        doc.setFillColor(255, 255, 255);
      } else {
        doc.setFillColor(248, 250, 252);
        doc.rect(MARGIN_X, y - 5, PAGE_W - MARGIN_X * 2, ROW_H, 'F');
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      escreverCelula(doc, formatarDataCurta(gasto.data), COLS.data, y);
      escreverCelula(doc, gasto.descricao, COLS.descricao, y);
      escreverCelula(doc, String(gasto.quantidade), COLS.quantidade, y);
      escreverCelula(doc, formatCurrency(gasto.valorUnitario), COLS.valorUnitario, y);
      doc.setFont('helvetica', 'bold');
      escreverCelula(doc, formatCurrency(totalDoGasto(gasto)), COLS.total, y);
      y += ROW_H;
    });

    quebrarPaginaSeNecessario(SUBTOTAL_H);
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN_X, y - 4, PAGE_W - MARGIN_X, y - 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(31, 111, 159);
    doc.text(`Subtotal ${grupo.categoria}`, COLS.valorUnitario.x, y);
    escreverCelula(doc, formatCurrency(grupo.subtotal), COLS.total, y);
    y += SUBTOTAL_H;
  });

  const totalPaginasReal = doc.getNumberOfPages();
  for (let pagina = 1; pagina <= totalPaginasReal; pagina += 1) {
    doc.setPage(pagina);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Gerado em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}`,
      MARGIN_X,
      PAGE_H - 10
    );
    doc.text(`Página ${pagina} de ${totalPaginasReal}`, PAGE_W - MARGIN_X, PAGE_H - 10, { align: 'right' });
  }

  return doc;
};

export const baixarPdfGastos = (gastos: Gasto[], ano: string, receita: number) => {
  const doc = gerarPdfGastos(gastos, ano, receita);
  doc.save(`gastos-${ano}.pdf`);
};
